import { Injectable, computed, inject } from "@angular/core";
import { injectQuery, injectQueryClient, queryOptions } from "@ngneat/query";
import { InvoiceService } from "../services/invoice.service";
import { Voucher } from "@features/order-actions/models/voucher.model";
import { firstValueFrom } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class VouchersState {
  private readonly invoiceService = inject(InvoiceService);
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();

  private getVouchersQueryOptions(tenantId: string, orderId: string) {
    return queryOptions({
      queryKey: ["vouchers", tenantId, orderId] as const,
      queryFn: async () => {
        return await firstValueFrom(
          this.invoiceService.getVouchersByOrder(tenantId, orderId),
        );
      },
    });
  }

  getVouchersQuery(tenantId: string | undefined, orderId: string | undefined) {
    const query = this.query({
      ...this.getVouchersQueryOptions(tenantId!, orderId!),
      enabled: !!tenantId && !!orderId,
    });

    return {
      result: query.result,
      result$: query.result$,
    };
  }

  hasCreditNotes(vouchers: Voucher[] | undefined): boolean {
    if (!vouchers || vouchers.length === 0) {
      return false;
    }
    return vouchers.some((v) => v.documentType === "NCR");
  }

  filterCreditNotes(vouchers: Voucher[] | undefined): Voucher[] {
    if (!vouchers || vouchers.length === 0) {
      return [];
    }
    return vouchers.filter((v) => v.documentType === "NCR");
  }

  async prefetchVouchers(tenantId: string, orderId: string) {
    return await this.queryClient.prefetchQuery(
      this.getVouchersQueryOptions(tenantId, orderId),
    );
  }
}
